/**
 * Cicha Pewność - mapa biur daje jeden spokojny wybór lokalizacji i przekazuje go do rezerwacji, bez rozproszonych list oraz osobnych ścieżek.
 */
import { useState } from "react";
import { CalendarCheck2, ExternalLink, MapPin, Phone } from "lucide-react";
import { Link } from "wouter";
import {
  grantFunctionalConsent,
  useCookiePreferences,
} from "@/lib/cookie-consent";
import {
  contact,
  getOfficeMapEmbedUrl,
  mappedOffices,
  type Office,
} from "@/lib/site-data";

export function OfficeExplorer() {
  const [selected, setSelected] = useState<Office>(mappedOffices[0]);
  const preferences = useCookiePreferences();
  const mapUrl = getOfficeMapEmbedUrl(selected);

  return (
    <section
      id="kancelarie"
      className="office-explorer section-pad"
      aria-labelledby="office-explorer-title"
    >
      <div className="shell-wide">
        <div className="section-lead">
          <span className="section-kicker">spotkanie w Twoim mieście</span>
          <h2 id="office-explorer-title">
            Nasze kancelarie - a w całej Polsce rozmowa po Twojemu.
          </h2>
          <p>
            Obsługujemy Klientów z całej Polski. Większość spraw prowadzimy w
            całości zdalnie: telefon, wideorozmowa, dokumenty online lub
            kurierem. Nie musisz przyjeżdżać do kancelarii.
          </p>
        </div>
        <div className="office-choice-panel">
          <p className="office-choice-label">Wybierz kancelarię</p>
          <div
            className="office-pin-list"
            role="group"
            aria-label="Lista kancelarii"
          >
            {mappedOffices.map(office => (
              <button
                key={office.city}
                type="button"
                onClick={() => setSelected(office)}
                className={selected.city === office.city ? "active" : ""}
              >
                <MapPin size={15} /> {office.city}
              </button>
            ))}
          </div>
          <p className="office-online-note">
            Najczęściej obsługujemy zdalnie Klientów m.in. z: Białegostoku,
            Bydgoszczy, Gdańska, Gorzowa Wielkopolskiego, Kielc, Krakowa,
            Lublina, Olsztyna, Opola, Poznania, Rzeszowa, Szczecina, Torunia,
            Wrocławia i Zielonej Góry. Nie widzisz swojej miejscowości?
            Obsługujemy całą Polskę - zadzwoń:{" "}
            <a href={contact.phoneHref}>513 513 613</a>.
          </p>
        </div>
        <div className="office-explorer-grid">
          <div className="office-map">
            {preferences.functional ? (
              <iframe
                className="office-map-embed"
                title={`Mapa dojazdu do kancelarii w ${selected.city}`}
                src={mapUrl}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            ) : (
              <div className="external-content-placeholder map-consent-placeholder">
                <div>
                  <p className="placeholder-kicker">Mapa kancelarii</p>
                  <h3>Sprawdź adresy naszych kancelarii</h3>
                  <ul>
                    {mappedOffices.map(office => (
                      <li key={office.slug}>
                        <strong>{office.city}</strong>
                        <span>{office.address}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="external-content-notice">
                  <p>
                    Załadowanie mapy połączy Cię z serwerami Google i przekaże
                    dostawcy adres IP.
                  </p>
                  <button
                    type="button"
                    className="button-primary"
                    onClick={grantFunctionalConsent}
                  >
                    Pokaż mapę
                  </button>
                </div>
              </div>
            )}
          </div>
          <aside className="selected-office-card">
            <div className="selected-office-label">
              <MapPin size={18} /> Wybrana kancelaria
            </div>
            <h3>{selected.city}</h3>
            <p>{selected.address}</p>
            <p className="office-detail">{selected.detail}</p>
            <div className="selected-office-actions">
              <Link href={`/upadlosc-konsumencka-${selected.slug}#kontakt`}>
                <CalendarCheck2 size={17} /> Umów rozmowę
              </Link>
              {selected.mapsUrl ? (
                <a
                  href={selected.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <MapPin size={16} /> Otwórz wizytówkę Google
                </a>
              ) : null}
              <a className="office-call-link" href={contact.phoneHref}>
                <Phone size={18} /> {contact.phoneDisplay}
              </a>
            </div>
          </aside>
        </div>
        <div
          className="office-listing-links"
          aria-label="Wizytówki Google kancelarii"
        >
          <p>Wizytówki Google naszych kancelarii</p>
          <ul>
            {mappedOffices.map(office =>
              office.mapsUrl ? (
                <li key={office.slug}>
                  <a
                    href={office.mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MapPin size={15} /> {office.city}
                    <ExternalLink size={14} aria-hidden="true" />
                  </a>
                </li>
              ) : null
            )}
          </ul>
        </div>
      </div>
    </section>
  );
}
